import React, { useCallback, useEffect } from "react";
import { useAppState } from "../contexts/AppStateContext";
import { useAuth } from "../hooks/useAuth";
import { useNavigation, useRoute, useFocusEffect } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { RootStackParamList } from "../navigation/AppNavigator.web";
import WebSocketNavigator from "../navigation/WebSocketNavigator";
import { useChatBar, ConversationType } from "../contexts/ChatBarContext";
import returnToUtils from "../utils/returnTo.utils";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useAppConfig } from "../AppConfigProvider";

const withAuth = (WrappedComponent) => {
  const WithAuthComponent = (props) => {
    const { state } = useAppState();
    const { isAuthenticated, isEmailVerified, otpVerifiedForSession, logout } = useAuth();
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
    const route = useRoute();
    const name = route.name;
    const params = route.params;
    const { mapRouteToContext, setActiveContextAndObjectId, setContext, setConversationType } = useChatBar();
    const appConfig = useAppConfig();
    const isCompanionMode = appConfig?.isCompanionMode;

    useEffect(() => {
      const checkToken = async () => {
        const accessToken = await AsyncStorage.getItem("accessToken");
        const refreshToken = await AsyncStorage.getItem("refreshToken");

        if (!refreshToken || !accessToken) {
          await logout();
        }
      };

      // without set timeout we can not gete the login redirect to work when user is using the Router navigation.
      setTimeout(async () => {
        checkToken();
      }, 2000); // 2 seconds. to ensure the tokens are set in the async storage.
    }, [route.name]);

    WebSocketNavigator();

    useFocusEffect(
      useCallback(() => {
        const activeContext = mapRouteToContext(name as keyof RootStackParamList, params);

        let objectId = null;
        switch (activeContext) {
          case "track":
            objectId = (params as RootStackParamList["Lesson Chat"]).loId;
            break;
          case "assignment":
          case "ai_tutor":
            objectId = (params as RootStackParamList["Resource"]).resource_id;
            break;
          case "journey":
            objectId = (params as RootStackParamList["Journey"])?.id || null;
            break;
          case "onboarding":
          case "general":
          default:
            objectId = null;
            break;
        }

        let contentType = null;
        switch (activeContext) {
          case "track":
            contentType = ConversationType.LEARNING;
            break;
          case "assignment":
          case "ai_tutor":
            contentType = ConversationType.SCHOOL;
            break;
          case "journey":
            contentType = ConversationType.JOURNEY;
            break;
          case "onboarding":
            contentType = ConversationType.ONBOARD;
            break;
          case "general":
          default:
            contentType = ConversationType.GENERAL_CONVERSATION;
            break;
        }

        setConversationType(contentType);
        setActiveContextAndObjectId({ context: activeContext, objectId });
        setContext({ context: activeContext, params: params as { [key: string]: string } });
      }, [name, params])
    );

    useFocusEffect(
      useCallback(() => {
        if (!isAuthenticated) {
          const currentUrl = window.location.pathname + window.location.search;
          returnToUtils.storeReturnToUrl(currentUrl);
          navigation.navigate("Login");
        } else if (!isEmailVerified || !otpVerifiedForSession) {
          const currentUrl = window.location.pathname + window.location.search;
          returnToUtils.storeReturnToUrl(currentUrl);
          navigation.navigate("Login");
        } else if (isCompanionMode && name === "Onboarding") {
          navigation.navigate("Home");
        }
      }, [isAuthenticated, isEmailVerified, otpVerifiedForSession, isCompanionMode, name])
    );

    if (!isAuthenticated) {
      return null;
    }

    return <WrappedComponent {...props} />;
  };

  return WithAuthComponent;
};

export default withAuth;
